import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: linear-gradient(135deg, #1a1033 0%, #2b1b54 50%, #392667 100%);
  color: #fff;
  position: relative;
`;

const Sidebar = styled.div`
  width: 240px;
  background: linear-gradient(180deg, rgba(26, 16, 51, 0.95) 0%, rgba(43, 27, 84, 0.95) 100%);
  padding: 0;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  z-index: 100;
  overflow-y: auto;
  backdrop-filter: blur(10px);
  border-right: 1px solid rgba(255, 255, 255, 0.1);

  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(26, 16, 51, 0.5);
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px 0;
  width: 100%;
  
  img {
    width: 100px;
    height: 100px;
    object-fit: contain;
  }
`;

const NavList = styled.div`
  padding: 20px 0;
`;

const NavGroup = styled.div`
  margin-bottom: 20px;
`;

const NavItem = styled(Link)`
  width: 100%;
  padding: 10px 20px;
  background: none;
  border: none;
  color: ${props => props.$active ? '#fff' : '#999'};
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  
  &:hover {
    color: #fff;
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: #E50914;
    opacity: ${props => props.$active ? 1 : 0};
    transition: opacity 0.2s ease;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const CategoryGroup = styled.div`
  margin-bottom: 20px;
`;

const CategoryHeader = styled.div`
  padding: 0 20px;
  margin-bottom: 10px;
  color: #999;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CategoryItem = styled.button`
  width: 100%;
  padding: 10px 20px;
  background: none;
  border: none;
  color: ${props => props.$active ? '#fff' : '#999'};
  font-size: 14px;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    color: #fff;
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: #E50914;
    opacity: ${props => props.$active ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: 240px;
  width: calc(100% - 240px);
  min-height: 100vh;
  position: relative;
  overflow-y: auto;
`;

const MainLayout = ({
  children,
  categories = [],
  selectedCategory,
  selectedType,
  onCategoryChange,
  onTypeChange
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Estados locais para persistência quando as props não são fornecidas
  const [storedCategories, setStoredCategories] = useState([]);
  const [storedSelectedCategory, setStoredSelectedCategory] = useState('');
  const [storedSelectedType, setStoredSelectedType] = useState('all');

  useEffect(() => {
    // Carregar grupos do localStorage se não fornecidos via props
    if (categories.length === 0) {
      const savedGroups = localStorage.getItem('iptv_groups');
      if (savedGroups) {
        try {
          setStoredCategories(JSON.parse(savedGroups));
        } catch (e) {
          console.error('Erro ao carregar categorias do storage', e);
        }
      }
    }

    // Carregar estado de seleção do localStorage se não fornecido via props
    if (selectedCategory === undefined) {
      const saved = localStorage.getItem('selectedCategory');
      if (saved !== null) setStoredSelectedCategory(saved);
    }

    if (selectedType === undefined) {
      const saved = localStorage.getItem('selectedType');
      if (saved !== null) setStoredSelectedType(saved);
    }
  }, [categories.length, selectedCategory, selectedType]);

  // Determinar valores a serem usados (props têm prioridade sobre estado local)
  const activeCategories = categories.length > 0 ? categories : storedCategories;
  const activeCategory = selectedCategory !== undefined ? selectedCategory : storedSelectedCategory;
  const activeType = selectedType !== undefined ? selectedType : storedSelectedType;

  // Handlers inteligentes que funcionam mesmo fora da Home
  const handleTypeChange = (type) => {
    if (onTypeChange) {
      onTypeChange(type);
    } else {
      localStorage.setItem('selectedType', type);
      localStorage.setItem('selectedCategory', ''); // Limpar categoria ao mudar tipo
      setStoredSelectedType(type);
      setStoredSelectedCategory('');
      navigate('/');
    }
  };

  const handleCategoryChange = (category) => {
    if (onCategoryChange) {
      onCategoryChange(category);
    } else {
      localStorage.setItem('selectedCategory', category);
      setStoredSelectedCategory(category);
      navigate('/');
    }
  };

  // Ordenar categorias alfabeticamente
  const sortedCategories = Array.from(activeCategories).sort((a, b) =>
    a.localeCompare(b, 'pt-BR')
  );

  return (
    <LayoutContainer>
      <Sidebar>
        <Logo>
          <img src="/logo_transparent.png" alt="Logo" />
        </Logo>

        <NavList>
          <NavGroup>
            <NavItem to="/" $active={location.pathname === '/'}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 22V12h6v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Início
            </NavItem>
            <NavItem to="/guide" $active={location.pathname === '/guide'}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 6h16M4 12h16M4 18h7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Guia de TV
            </NavItem>
            <NavItem to="/favorites" $active={location.pathname === '/favorites'}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2v16z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Favoritos
            </NavItem>
            <NavItem to="/settings" $active={location.pathname === '/settings'}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Configurações
            </NavItem>
          </NavGroup>

          <CategoryGroup>
            <CategoryItem
              $active={activeType === 'channels'}
              onClick={() => handleTypeChange('channels')}
            >
              Canais
            </CategoryItem>
            <CategoryItem
              $active={activeType === 'movies'}
              onClick={() => handleTypeChange('movies')}
            >
              Filmes
            </CategoryItem>
            <CategoryItem
              $active={activeType === 'series'}
              onClick={() => handleTypeChange('series')}
            >
              Séries
            </CategoryItem>
          </CategoryGroup>

          {activeType !== 'all' && activeCategories.length > 0 && (
            <CategoryGroup>
              <CategoryHeader>Categorias</CategoryHeader>
              <CategoryItem
                $active={activeCategory === ''}
                onClick={() => handleCategoryChange('')}
              >
                Todas as Categorias
              </CategoryItem>
              {sortedCategories
                .filter(category => {
                  const categoryLower = category.toLowerCase();

                  // Verificar se é um canal de filmes 24h (que deve ser classificado como canal)
                  const isMovieChannel =
                    // Canais de filmes 24h específicos
                    (categoryLower.includes('filmes') &&
                      (categoryLower.includes('24h') ||
                        categoryLower.includes('selecionados') ||
                        categoryLower.includes('e séries') ||
                        categoryLower.includes('filmes e') ||
                        categoryLower.includes('filmes 24'))) ||
                    // Outros canais de filmes que devem ser classificados como canais
                    (categoryLower.includes('filmes') && categoryLower.includes('canais'));

                  // Verificar se é explicitamente um canal
                  const isExplicitChannel = categoryLower.includes('canais');

                  // Verificar se é um dos itens específicos mencionados pelo usuário
                  const isSpecificItem =
                    categoryLower.includes('filmes e séries') ||
                    categoryLower.includes('filmes selecionados');

                  if (activeType === 'channels') {
                    // Incluir tanto categorias com "canais" quanto canais de filmes 24h
                    // Também incluir os itens específicos mencionados pelo usuário
                    return isExplicitChannel || isMovieChannel || isSpecificItem;
                  }
                  if (activeType === 'movies') {
                    // Excluir canais de filmes 24h e os itens específicos da categoria de filmes
                    return categoryLower.includes('filmes') && !isMovieChannel && !isExplicitChannel && !isSpecificItem;
                  }
                  if (activeType === 'series') {
                    // Excluir canais de séries da categoria de séries
                    return categoryLower.includes('series') &&
                      !categoryLower.includes('e séries') &&
                      !isExplicitChannel &&
                      !isSpecificItem;
                  }
                  return true;
                })
                .map(category => (
                  <CategoryItem
                    key={category}
                    $active={activeCategory === category}
                    onClick={() => handleCategoryChange(category)}
                  >
                    {category.replace(/^(Canais|Filmes|Series)\s*\|\s*/, '')}
                  </CategoryItem>
                ))}
            </CategoryGroup>
          )}
        </NavList>
      </Sidebar>

      <MainContent>
        {children}
      </MainContent>
    </LayoutContainer>
  );
};

export default MainLayout; 